import { fromJS, Map, OrderedMap } from 'immutable';
import { mergeEntity, normalizeEntities } from 'helpers/utilHelpers';

import {
  ADD_PROJECT_FAVORITE_SUCCESS,
  CREATE_PROJECT_SUCCESS,
  CREATE_TEAM_MAP_SUCCESS,
  DELETE_PROJECT_FAVORITE_SUCCESS,
  DELETE_PROJECT_SUCCESS,
  DELETE_SERVICE_MAP_SUCCESS,
  DELETE_TASK_SUCCESS,
  DELETE_TEAM_MAP_SUCCESS,
  LOAD_CLIENT_BY_ID_SUCCESS,
  LOAD_CLIENTS_SUCCESS,
  LOAD_DISMISS_USER_SUCCESS,
  LOAD_PROJECT_BY_ID_SUCCESS,
  LOAD_PROJECTS_DRIVE_FOLDERS_SUCCESS,
  LOAD_PROJECTS_FAVORITE_MAP_SUCCESS,
  LOAD_PROJECTS_SERVICES_MAP_SUCCESS,
  LOAD_SERVICE_ANALYTICS_SUCCESS,
  LOAD_PROJECTS_SUCCESS,
  LOAD_TASK_BY_ID_SUCCESS,
  RUN_PROJECT_MONITORING_SUCCESS,
  SYNC_PROJECT_DRIVE,
  SYNC_PROJECT_DRIVE_SUCCESS,
  UPDATE_PROJECT_MONITORING_SETTINGS_SUCCESS,
  CONNECT_PROJECT_GOOGLE_SEARCH_SUCCESS,
  UPDATE_PROJECT_SUCCESS,
  UPDATE_SERVICE_MAP_SUCCESS,
  UPDATE_TASK_SUCCESS,
  LOAD_PROJECTS_IMPORTANT_PAGES_SUCCESS,
  UPDATE_PROJECTS_IMPORTANT_PAGE_SUCCESS,
  DELETE_PROJECTS_IMPORTANT_PAGE_SUCCESS,
  BATCH_UPDATE_PROJECTS_IMPORTANT_PAGE_SUCCESS,
  LOAD_PROJECT_SERVICE_HISTORY_SUCCESS,
  UPDATE_PROJECT_SERVICE_HISTORY_SUCCESS,
  UPLOAD_ENTITY_FILE_SUCCESS,
  LOAD_SEARCH_COMMENTS_SUCCESS,
  LOAD_CLIENTS_TABLE_SUCCESS,
  DELETE_WORKFLOW_SUCCESS,
} from 'constants/ActionTypes';

const initialState = fromJS({
  projects: {},
  projectsFavoritesMap: {},
  projectsServicesMap: {},
  projectServiceHistory: {},
  projectsImportantPages: {},
  teamMaps: {},
  isDriveSyncing: false,
  driveFolders: null,
});

/**
 * Function for change array field: `team`, `clientProfiles` and `services` in project
 * @param project
 * @param team - array
 * @param services - array
 * @param favorites - array
 * @return new project
 */
const prepareProject = (project, team = null, services = null, favorites = null) => {
  const excludingProject = ['client', 'tasks'];

  if (project.team) {
    project.team = project.team.map((memberTeam) => {
      team && team.push(memberTeam);
      return memberTeam.mapId;
    });
  }

  if (project.clientProfiles) {
    project.clientProfiles = project.clientProfiles.map((profile) => profile.userId);
  }

  if (project.services) {
    project.services = project.services.map((service) => {
      services && services.push(prepareServiceMap({ ...service }));
      return service.relationId;
    });
  }

  if (project.favoriteMap) {
    project.favoriteMap = project.favoriteMap.map((map) => {
      favorites && favorites.push(map);
      return map.mapId;
    });
  }

  if (project.files?.length) {
    project.files = project.files.map((file) => file.fileId);
  }

  excludingProject.forEach((field) => project[field] && delete project[field]);

  return project;
};

/**
 * Function for prepare service map item
 * @param serviceMap
 * @return {*}
 */
const prepareServiceMap = (serviceMap) => {
  const excludingService = ['client', 'project', 'service'];

  excludingService.forEach((field) => delete serviceMap[field]);

  return serviceMap;
};

const projectReducer = (state = initialState, action) => {
  const teamMap = [];
  const serviceMap = [];
  const favoriteMap = [];

  switch (action.type) {
    case LOAD_TASK_BY_ID_SUCCESS: {
      if (action.task.project) {
        const project = prepareProject({ ...action.task.project }, teamMap, serviceMap);

        return state
          .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
          .setIn(['projects', project.projectId], mergeEntity(project, state.getIn(['projects', project.projectId])))
          .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
      }
      return state;
    }
    case LOAD_PROJECTS_SUCCESS: {
      const projects = action.projects.items.map((project) => prepareProject({ ...project }, teamMap, serviceMap));

      return state
        .set('projects', normalizeEntities(projects, 'projectId', state.get('projects')))
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
    }
    case LOAD_SERVICE_ANALYTICS_SUCCESS:
    case LOAD_PROJECTS_SERVICES_MAP_SUCCESS: {
      let projects = [];

      action.projectsServicesMap.items.forEach((map) => {
        serviceMap.push(prepareServiceMap({ ...map }));
        map.project && projects.push(prepareProject({ ...map.project }, teamMap));
      });

      return state
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')))
        .set('projects', normalizeEntities(projects, 'projectId', state.get('projects')))
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')));
    }
    case LOAD_PROJECT_BY_ID_SUCCESS: {
      const project = prepareProject({ ...action.project }, teamMap, serviceMap, favoriteMap);

      return state
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
        .setIn(['projects', project.projectId], mergeEntity(project, state.getIn(['projects', project.projectId])))
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')))
        .set('projectsFavoritesMap', normalizeEntities(favoriteMap, 'mapId', state.get('projectsFavoritesMap')));
    }
    case LOAD_CLIENTS_SUCCESS: {
      let projects = [];

      action.clients.items.forEach((client) => {
        client.projects &&
          client.projects.forEach((project) => {
            projects.push(prepareProject({ ...project }, teamMap, serviceMap));
          });
      });

      return state
        .set('projects', normalizeEntities(projects, 'projectId', state.get('projects')))
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
    }
    case UPDATE_PROJECT_SUCCESS: {
      const projectPath = ['projects', action.project.projectId];
      if (!state.getIn(projectPath)) {
        return state;
      }

      const project = prepareProject({ ...action.project }, teamMap);
      return state
        .setIn(projectPath, mergeEntity(project, state.getIn(projectPath)))
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')));
    }
    case CREATE_PROJECT_SUCCESS: {
      const project = prepareProject({ ...action.project }, teamMap, serviceMap);

      return state
        .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
        .setIn(['projects', project.projectId], mergeEntity(project, state.getIn(['projects', project.projectId])))
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
    }
    case UPDATE_SERVICE_MAP_SUCCESS: {
      let newState = state.setIn(['projectsServicesMap', action.serviceMap.relationId], fromJS(action.serviceMap));

      if (action.serviceMap.lastHistories) {
        newState = newState.set(
          'projectServiceHistory',
          normalizeEntities(action.serviceMap.lastHistories, 'historyId', state.get('projectServiceHistory')),
        );
      }

      if (action.serviceMap.teamMaps) {
        newState = newState.set(
          'teamMaps',
          normalizeEntities(action.serviceMap.teamMaps, 'mapId', state.get('teamMaps')),
        );

        const newProjectTeam = action.serviceMap.teamMaps.map((member) => parseInt(member.mapId));
        newState = newState.setIn(['projects', action.serviceMap.projectId, 'team'], fromJS(newProjectTeam));

        // Update services in project
        let services = newState.getIn(['projects', action.serviceMap.projectId, 'services']);
        newState = newState.setIn(
          ['projects', action.serviceMap.projectId, 'services'],
          services
            ? services.includes(action.serviceMap.relationId)
              ? services
              : services.push(action.serviceMap.relationId)
            : fromJS([action.serviceMap.relationId]),
        );
      }
      return newState;
    }
    case DELETE_SERVICE_MAP_SUCCESS: {
      const serviceMap = state.getIn(['projectsServicesMap', action.relationId]);
      let project = state.getIn(['projects', serviceMap.get('projectId')]);
      project = project.set(
        'services',
        project.get('services').filter((id) => id !== action.relationId),
      );

      return state
        .setIn(['projects', project.get('projectId')], project)
        .deleteIn(['projectsServicesMap', action.relationId]);
    }
    case CREATE_TEAM_MAP_SUCCESS: {
      const teamMap = { ...action.teamMap };
      let project = state.getIn(['projects', teamMap.projectId]);
      project = project.set('team', project.get('team').push(teamMap.mapId));

      return state
        .setIn(['projects', project.get('projectId')], project)
        .setIn(['teamMaps', teamMap.mapId], fromJS(teamMap));
    }
    case DELETE_TEAM_MAP_SUCCESS: {
      const projectId = state.getIn(['teamMaps', action.mapId, 'projectId']);
      const teamIds = state.getIn(['projects', projectId, 'team']).filter((id) => id !== action.mapId);

      return state.setIn(['projects', projectId, 'team'], teamIds).deleteIn(['teamMaps', action.mapId]);
    }
    case LOAD_DISMISS_USER_SUCCESS: {
      const projects = [];

      if (action.user.projectServiceMaps) {
        action.user.projectServiceMaps.forEach((map) => serviceMap.push(prepareServiceMap({ ...map })));
      }

      if (action.user.projects) {
        action.user.projects.forEach((project) => projects.push(prepareProject({ ...project })));
      }

      return state
        .set('projects', normalizeEntities(projects, 'projectId', state.get('projects')))
        .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
    }
    case LOAD_CLIENT_BY_ID_SUCCESS: {
      if (action.values.projects) {
        const projects = [];
        action.values.projects.forEach((project) => projects.push(prepareProject({ ...project }, teamMap, serviceMap)));
        return state
          .set('projects', normalizeEntities(projects, 'projectId', state.get('projects')))
          .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
          .set('projectsServicesMap', normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')));
      }

      return state;
    }
    case DELETE_PROJECT_SUCCESS:
      return state.deleteIn(['projects', action.projectId]);
    case ADD_PROJECT_FAVORITE_SUCCESS: {
      let result = state.setIn(['projectsFavoritesMap', action.favoriteMap.mapId], fromJS(action.favoriteMap));
      if (state.getIn(['projects', action.favoriteMap.projectId, 'favoriteMap'])) {
        result = result.mergeIn(['projects', action.favoriteMap.projectId, 'favoriteMap'], action.favoriteMap.mapId);
      }
      return result;
    }
    case DELETE_PROJECT_FAVORITE_SUCCESS: {
      const map = state
        .get('projectsFavoritesMap')
        .find((map) => map.get('entity') === action.values.entity && map.get('entityId') === action.values.entityId);
      let favoriteIds = state.getIn(['projects', map.get('projectId'), 'favoriteMap']);
      if (map && favoriteIds) {
        favoriteIds = favoriteIds.filter((id) => id !== map.get('mapId'));
        return state
          .setIn(['projects', map.get('projectId'), 'favoriteMap'], favoriteIds)
          .deleteIn(['projectsFavoritesMap', map.get('mapId')]);
      }
      return state.deleteIn(['projectsFavoritesMap', map.get('mapId')]);
    }
    case LOAD_PROJECTS_FAVORITE_MAP_SUCCESS:
      return state.set(
        'projectsFavoritesMap',
        normalizeEntities(action.projectsFavoriteMap.items, 'mapId', state.get('projectsFavoritesMap')),
      );
    case LOAD_PROJECTS_DRIVE_FOLDERS_SUCCESS:
      if (action.folders) {
        let driveFolders = new Map();
        if (typeof action.folders === 'object') {
          driveFolders = new OrderedMap();
          Object.keys(action.folders).forEach((key) => {
            driveFolders = driveFolders.set(key, action.folders[key]);
          });
        }
        return state.set('driveFolders', driveFolders);
      }
      return state;
    case SYNC_PROJECT_DRIVE:
      return state.set('isDriveSyncing', true);
    case SYNC_PROJECT_DRIVE_SUCCESS: {
      const connected = state.getIn(['projects', action.projectId, 'isGoogleDriveConnected']);
      return state
        .set('isDriveSyncing', false)
        .setIn(['projects', action.projectId, 'isGoogleDriveConnected'], connected ? connected : true);
    }
    case UPDATE_TASK_SUCCESS: {
      if (action.task?.projectId) {
        const project = state.getIn(['projects', parseInt(action.task.projectId)]);
        if (project && project.get('lastTasks')) {
          const taskIndex = project.get('lastTasks').findIndex((task) => task?.get('taskId') === action.task.taskId);
          if (taskIndex !== -1) {
            if (action.task.status !== 'active') {
              return state.setIn(
                ['projects', project.get('projectId'), 'lastTasks'],
                project.get('lastTasks').delete(taskIndex),
              );
            }
            return state.setIn(['projects', project.get('projectId'), 'lastTasks', taskIndex], fromJS(action.task));
          }
          return state.setIn(
            ['projects', project.get('projectId'), 'lastTasks'],
            project.get('lastTasks').push(fromJS(action.task)),
          );
        }
      }
      return state;
    }
    case DELETE_TASK_SUCCESS: {
      if (action.projectId) {
        const project = state.getIn(['projects', parseInt(action.projectId)]);
        if (project && project.get('lastTasks')) {
          return state.setIn(
            ['projects', project.get('projectId'), 'lastTasks'],
            project
              .get('lastTasks')
              .delete(project.get('lastTasks').findIndex((task) => task?.get('taskId') === action.taskId)),
          );
        }
      }
      return state;
    }
    case UPDATE_PROJECT_MONITORING_SETTINGS_SUCCESS: {
      let newState = state;
      action.values.projectIds.forEach((projectId) => {
        newState = newState.setIn(['projects', projectId, 'monitoringOptions'], fromJS(action.values.settings));
      });
      return newState;
    }
    case RUN_PROJECT_MONITORING_SUCCESS:
      return state.setIn(['projects', action.statistic.projectId, 'yandexStatistics'], fromJS(action.statistic));
    case LOAD_PROJECTS_IMPORTANT_PAGES_SUCCESS:
      return state.set(
        'projectsImportantPages',
        normalizeEntities(action.pages.items, 'pageId', state.get('projectsImportantPages')),
      );
    case UPDATE_PROJECTS_IMPORTANT_PAGE_SUCCESS: {
      let newState = state;
      if (action.pages?.length) {
        newState = newState.set(
          'projectsImportantPages',
          normalizeEntities(action.pages, 'pageId', state.get('projectsImportantPages')),
        );
      }
      if (typeof action.pages !== 'boolean' && action.pages.pageId) {
        newState = newState.setIn(['projectsImportantPages', action.pages.pageId], fromJS(action.pages));
      }
      return newState;
    }
    case BATCH_UPDATE_PROJECTS_IMPORTANT_PAGE_SUCCESS: {
      if (action.values.action === 'update') {
        return state.set(
          'projectsImportantPages',
          normalizeEntities(action.values.items, 'pageId', state.get('projectsImportantPages')),
        );
      }
      if (action.values.action === 'delete') {
        return state.set(
          'projectsImportantPages',
          state.get('projectsImportantPages').filter((page) => !action.values.items.includes(page.get('pageId'))),
        );
      }
      return state;
    }
    case DELETE_PROJECTS_IMPORTANT_PAGE_SUCCESS: {
      return state.deleteIn(['projectsImportantPages', action.pageId]);
    }
    case CONNECT_PROJECT_GOOGLE_SEARCH_SUCCESS: {
      if (!action.projectId) {
        return state;
      }

      return state.setIn(
        ['projects', parseInt(action.projectId), 'isGoogleSearchConnected'],
        action.isGoogleSearchConnected,
      );
    }
    case LOAD_PROJECT_SERVICE_HISTORY_SUCCESS: {
      return state.set(
        'projectServiceHistory',
        normalizeEntities(action.response.items, 'historyId', state.get('projectServiceHistory')),
      );
    }
    case UPDATE_PROJECT_SERVICE_HISTORY_SUCCESS:
      if (action.response?.historyId) {
        return state.setIn(['projectServiceHistory', action.response.historyId], fromJS(action.response));
      }
      return state;
    case UPLOAD_ENTITY_FILE_SUCCESS: {
      return state.setIn(
        ['projects', parseInt(action.response.file.projectId), 'files'],
        state.getIn(['projects', parseInt(action.response.file.projectId), 'files']).push(action.response.file.fileId),
      );
    }
    case LOAD_SEARCH_COMMENTS_SUCCESS: {
      const team = [];
      action.comments.items.forEach((comment) => {
        if (comment?.team?.length) {
          comment.team.forEach((memberTeam) => {
            team.push(memberTeam);
          });
        }
      });
      return state.set('teamMaps', normalizeEntities(team, 'mapId', state.get('teamMaps')));
    }
    case LOAD_CLIENTS_TABLE_SUCCESS: {
      let newState = state;
      action.clients.items.forEach((client) => {
        if (client.projects?.length) {
          client.projects.forEach((project) => {
            const preparedProject = prepareProject({ ...project }, teamMap, serviceMap, favoriteMap);

            newState = newState
              .set('teamMaps', normalizeEntities(teamMap, 'mapId', state.get('teamMaps')))
              .setIn(
                ['projects', preparedProject.projectId],
                mergeEntity(preparedProject, state.getIn(['projects', preparedProject.projectId])),
              )
              .set(
                'projectsServicesMap',
                normalizeEntities(serviceMap, 'relationId', state.get('projectsServicesMap')),
              );
          });
        }
      });

      return newState;
    }

    case DELETE_WORKFLOW_SUCCESS: {
      return state.set(
        'projects',
        state.get('projects').map((project) => {
          if (project.get('workflowId') === action.workflowId) {
            return project.set('workflowId', null);
          }

          return project;
        }),
      );
    }
    default:
      return state;
  }
};

export default projectReducer;
